import { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Upload as UploadIcon, FileText, X, Link2, Cloud } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function Upload({ api }) {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [importUrl, setImportUrl] = useState("");
  const [uploadMode, setUploadMode] = useState("file");

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === "application/pdf") {
      setFile(droppedFile);
      if (!title) setTitle(droppedFile.name.replace(".pdf", ""));
    } else {
      toast.error("Please drop a PDF file");
    }
  }, [title]);

  const handleFileSelect = (e) => {
    const selected = e.target.files[0];
    if (selected) {
      setFile(selected);
      if (!title) setTitle(selected.name.replace(".pdf", ""));
    }
  };

  const handleUpload = async () => {
    if (!title.trim()) {
      toast.error("Please enter a title");
      return;
    }
    setUploading(true);
    try {
      if (uploadMode === "file") {
        if (!file) {
          toast.error("Please select a file");
          setUploading(false);
          return;
        }
        const formData = new FormData();
        formData.append("file", file);
        formData.append("title", title.trim());
        const res = await axios.post(`${api}/papers/upload`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
          withCredentials: true,
        });
        toast.success("Paper uploaded! Let's start reading.");
        navigate(`/reader/${res.data.id}`);
      } else {
        if (!importUrl.trim()) {
          toast.error("Please enter a URL");
          setUploading(false);
          return;
        }
        const formData = new FormData();
        formData.append("url", importUrl.trim());
        formData.append("title", title.trim());
        const res = await axios.post(`${api}/papers/import-url`, formData, {
          withCredentials: true,
        });
        toast.success("Paper imported! Let's start reading.");
        navigate(`/reader/${res.data.id}`);
      }
    } catch (e) {
      const msg = e.response?.data?.detail || "Upload failed";
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="upload-page">
      <div>
        <h1 className="font-heading font-extrabold text-4xl sm:text-5xl tracking-tight dark:text-white">Upload Paper</h1>
        <p className="text-neo-text-secondary dark:text-gray-400 mt-2 text-base">Drop your journal article or import from cloud storage</p>
      </div>

      {/* Upload Mode Tabs */}
      <Tabs value={uploadMode} onValueChange={setUploadMode} className="w-full">
        <TabsList className="grid w-full grid-cols-2 border-2 border-neo-border dark:border-[#444] rounded-lg overflow-hidden p-0 h-auto bg-white dark:bg-[#2A2A2A]">
          <TabsTrigger
            value="file"
            className="py-3 rounded-none font-heading font-bold text-sm data-[state=active]:bg-neo-yellow data-[state=active]:text-neo-text dark:text-gray-300 border-r-2 border-neo-border dark:border-[#444]"
            data-testid="upload-tab-file"
          >
            <UploadIcon className="w-4 h-4 mr-2" />
            Upload File
          </TabsTrigger>
          <TabsTrigger
            value="url"
            className="py-3 rounded-none font-heading font-bold text-sm data-[state=active]:bg-neo-yellow data-[state=active]:text-neo-text dark:text-gray-300"
            data-testid="upload-tab-url"
          >
            <Cloud className="w-4 h-4 mr-2" />
            Import from URL
          </TabsTrigger>
        </TabsList>

        <TabsContent value="file" className="mt-4">
          <div
            className={`neo-card dark:bg-[#1E1E1E] dark:border-[#444] dark:shadow-[4px_4px_0px_0px_#444] cursor-pointer transition-all ${dragOver ? "border-neo-yellow bg-yellow-50 dark:bg-yellow-900/20 shadow-[6px_6px_0px_0px_#1A1A1A] dark:shadow-[6px_6px_0px_0px_#444]" : ""}`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileRef.current?.click()}
            data-testid="upload-dropzone"
          >
            <input type="file" ref={fileRef} className="hidden" accept=".pdf" onChange={handleFileSelect} data-testid="upload-file-input" />
            {file ? (
              <div className="flex items-center gap-4 py-4">
                <div className="w-14 h-14 rounded-lg bg-neo-green border-2 border-neo-border dark:border-[#444] shadow-[2px_2px_0px_0px_#1A1A1A] dark:shadow-[2px_2px_0px_0px_#444] flex items-center justify-center flex-shrink-0">
                  <FileText className="w-7 h-7 text-neo-text" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-heading font-bold text-lg truncate dark:text-white">{file.name}</p>
                  <p className="text-sm text-neo-text-secondary dark:text-gray-400">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                </div>
                <button onClick={(e) => { e.stopPropagation(); setFile(null); }} className="neo-btn bg-white dark:bg-[#2A2A2A] dark:border-[#444] dark:text-white p-2" data-testid="remove-file-btn">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-full bg-neo-yellow border-2 border-neo-border dark:border-[#444] shadow-[2px_2px_0px_0px_#1A1A1A] dark:shadow-[2px_2px_0px_0px_#444] flex items-center justify-center mx-auto mb-4">
                  <UploadIcon className="w-8 h-8 text-neo-text" />
                </div>
                <p className="font-heading font-bold text-xl mb-1 dark:text-white">Drop your PDF here</p>
                <p className="text-sm text-neo-text-secondary dark:text-gray-400">or click to browse files</p>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="url" className="mt-4">
          <div className="neo-card dark:bg-[#1E1E1E] dark:border-[#444] dark:shadow-[4px_4px_0px_0px_#444] space-y-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-neo-purple border-2 border-neo-border dark:border-[#444] shadow-[2px_2px_0px_0px_#1A1A1A] dark:shadow-[2px_2px_0px_0px_#444] flex items-center justify-center">
                <Link2 className="w-5 h-5 text-neo-text" />
              </div>
              <div>
                <p className="font-heading font-bold dark:text-white">Import from Cloud</p>
                <p className="text-xs text-neo-text-secondary dark:text-gray-400">Paste a shared link from Dropbox, Google Drive, or any direct PDF URL</p>
              </div>
            </div>
            <Input
              value={importUrl}
              onChange={(e) => setImportUrl(e.target.value)}
              placeholder="https://www.dropbox.com/s/... or https://drive.google.com/..."
              className="border-2 border-neo-border dark:border-[#444] dark:bg-[#2A2A2A] dark:text-white rounded-lg px-4 py-3 font-body text-base focus:ring-0 focus:border-[3px] focus:border-neo-yellow"
              data-testid="import-url-input"
            />
            <div className="flex flex-wrap gap-2">
              <span className="neo-badge text-xs bg-neo-blue">Dropbox</span>
              <span className="neo-badge text-xs bg-neo-green">Google Drive</span>
              <span className="neo-badge text-xs bg-neo-purple">Direct PDF URL</span>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Paper Details */}
      <div className="neo-card dark:bg-[#1E1E1E] dark:border-[#444] dark:shadow-[4px_4px_0px_0px_#444] space-y-4">
        <div>
          <label className="text-sm uppercase tracking-[0.15em] font-semibold text-neo-text-secondary dark:text-gray-400 block mb-2">Paper Title</label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Enter paper title..."
            className="border-2 border-neo-border dark:border-[#444] dark:bg-[#2A2A2A] dark:text-white rounded-lg px-4 py-3 font-body text-base focus:ring-0 focus:border-[3px] focus:border-neo-yellow"
            data-testid="paper-title-input"
          />
        </div>
      </div>

      <button
        onClick={handleUpload}
        disabled={!title.trim() || uploading || (uploadMode === "file" && !file) || (uploadMode === "url" && !importUrl.trim())}
        className={`neo-btn w-full text-lg py-4 ${
          !title.trim() || uploading || (uploadMode === "file" && !file) || (uploadMode === "url" && !importUrl.trim())
            ? "bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed border-gray-300 dark:border-gray-600 shadow-none"
            : "neo-btn-primary"
        }`}
        data-testid="upload-submit-btn"
      >
        {uploading ? (
          <span className="flex items-center justify-center gap-2">
            <div className="w-5 h-5 border-2 border-neo-text border-t-transparent rounded-full animate-spin" />
            {uploadMode === "url" ? "Importing PDF..." : "Processing PDF..."}
          </span>
        ) : (
          "Start Reading Quest"
        )}
      </button>
    </div>
  );
}
